import type { ManipulateType, OpUnitType, PluginFunc, QUnitType, UnitType } from 'dayjs/esm'
import type { ResolutionEnum } from '~/lib/gantt'

type Unit = QUnitType | OpUnitType | ResolutionEnum

declare module 'dayjs/esm' {
  interface Dayjs {
    add(value: number, unit: QUnitType | ResolutionEnum): Dayjs
    subtract(value: number, unit: QUnitType | ResolutionEnum): Dayjs

    startOf(unit: QUnitType | OpUnitType | ResolutionEnum): Dayjs
    endOf(unit: QUnitType | OpUnitType | ResolutionEnum): Dayjs // internally uses startOf

    isSame(date: ConfigType, unit?: QUnitType | ResolutionEnum): boolean // internally uses startOf+endOf
    isSameOrAfter(date: ConfigType, unit?: QUnitType | ResolutionEnum): boolean
    isSameOrBefore(date: ConfigType, unit?: QUnitType | ResolutionEnum): boolean

    isBefore(date: ConfigType, unit?: QUnitType | ResolutionEnum): boolean // internally uses endOf
    isAfter(date: ConfigType, unit?: QUnitType | ResolutionEnum): boolean // internally uses startOf

    diff(date: ConfigType, unit?: QUnitType | ResolutionEnum, float?: boolean): number // apparently uses add/subtract
  }
}

/**
 * Extends dayjs to use the unit "twoYears" for several functions
 */
const twoYears: PluginFunc = (_, dayjsClass) => {
  const proto = dayjsClass.prototype

  /**
   * Allows addition of unit "twoYears"
   */
  const oldAdd = proto.add
  proto.add = function (number, unit: any = undefined) {
    number = Number(number)
    if (unit === 'twoYears') {
      return this.add(number * 2, 'year')
    }
    return oldAdd.bind(this)(number, unit)
  }

  /**
   * Allows subtraction of unit "twoYears"
   */
  const oldSubtract = proto.subtract
  proto.subtract = function (number, unit: any = undefined) {
    number = Number(number)

    if (unit === 'twoYears') {
      return this.subtract(number * 2, 'year')
    }

    return oldSubtract.bind(this)(number, unit)
  }

  /**
   * Allows startOf of unit "twoYears"
   */
  const oldStartOf = proto.startOf
  proto.startOf = function (...args) {
    const unit = args[0]
    // @ts-expect-error this is actually an internal parameter: https://github.com/iamkun/dayjs/blob/dev/src/index.js#L146
    const isStartOf = args[1]
    if (unit === 'twoYears') {
      if (isStartOf) {
        const year = this.year() % 2 === 0 ? this.year() : this.year() - 1
        return this.year(year).startOf('day')
      }
      const year = this.year() % 2 === 0 ? this.year() + 1 : this.year()
      return this.year(year).endOf('year').endOf('month').endOf('day')
    }
    // @ts-expect-error see above comment
    return oldStartOf.bind(this)(unit, isStartOf)
  }
}
export default twoYears
